import * as React from 'react';
import { Area, ComposedChart, CartesianGrid, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { cx } from '../../../utils/cx';
import { formatMoneyShort, formatOrdinal } from '../../../utils/formatting';
import AnimationFixToolTip from '../../base/AnimationFixToolTip';
import { CHART_NO_FOCUS_OUTLINE, DEFAULT_CHART_HEIGHT } from '../../base/charts/ChartsBase';
import LoadingIndicator from '../../base/LoadingIndicator';

import { processDailyCumulativeSpendData } from './dataProcessing';
import { MonthlySpendComparisonChartHeader } from './MonthlySpendComparisonChartHeader';

import type { IDailyCumulativeSpendDataPoint } from './dataProcessing';
import type { ColumnTable } from 'arquero';

/**
 * Custom tooltip content for MonthlySpendComparisonChart
 * Shows cumulative spending for current month and last month at the hovered day
 */
const MonthlySpendComparisonChartTooltipContent = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number | null;
    dataKey: string;
    payload: IDailyCumulativeSpendDataPoint;
  }>;
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) {
    return null;
  }

  const dayLabel = `Day ${dataPoint.day}`;
  const currentSpending = dataPoint.spending !== null ? formatMoneyShort(dataPoint.spending) : null;
  const previousSpending = dataPoint.previousSpending !== null ? formatMoneyShort(dataPoint.previousSpending) : null;

  // Don't show tooltip if both values are null
  if (currentSpending === null && previousSpending === null) {
    return null;
  }

  return (
    <div className="bg-primary-solid flex flex-col gap-0.5 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-utility-gray-300 text-[9px] font-semibold uppercase">{dayLabel}</p>
      <div className="flex flex-col gap-0.5">
        {/* This Month */}
        {currentSpending !== null && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <svg width="12" height="4" className="text-utility-brand-600">
                <line x1="0" y1="2" x2="12" y2="2" stroke="currentColor" strokeWidth="2" />
              </svg>
              <p className="text-tooltip-supporting-text text-xs font-medium">This month</p>
            </div>
            <p className="text-tooltip-supporting-text text-right text-xs font-semibold">{currentSpending}</p>
          </div>
        )}
        {/* Last Month */}
        {previousSpending !== null && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <svg width="12" height="4" className="text-utility-brand-400">
                <line x1="0" y1="2" x2="12" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="3 1" />
              </svg>
              <p className="text-tooltip-supporting-text text-xs font-medium">Last month</p>
            </div>
            <p className="text-tooltip-supporting-text text-right text-xs font-semibold">{previousSpending}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export interface IMonthlySpendComparisonChartProps {
  transactions: ColumnTable | null;
  className?: string;
  isLoading?: boolean;
}

const MonthlySpendComparisonChart = ({
  transactions,
  className = '',
  isLoading = false,
}: IMonthlySpendComparisonChartProps) => {
  const { containerRef } = useContainerWidth();

  // Process the data to get daily cumulative spending
  const { chartData, currentTotal, previousTotal, xAxisTicks } = React.useMemo(() => {
    if (!transactions) {
      return {
        chartData: [],
        currentTotal: 0,
        previousTotal: 0,
        xAxisTicks: [1, 9, 16, 24, 31],
      };
    }

    const result = processDailyCumulativeSpendData(transactions);

    // Calculate max day as maximum of current day and last month's total days
    const maxDay = Math.max(result.currentMonthDaysElapsed, result.lastMonthTotalDays);

    // Filter chart data to only include days up to maxDay
    const filteredChartData = result.chartData.filter((d) => d.day <= maxDay);

    // Build x-axis ticks with dynamic last value
    const baseTicks = [1, 9, 16, 24];
    const xAxisTicks = maxDay > 24 ? [...baseTicks, maxDay] : baseTicks.filter((t) => t <= maxDay);

    return {
      chartData: filteredChartData,
      currentTotal: result.currentTotal,
      previousTotal: result.previousTotal,
      xAxisTicks,
    };
  }, [transactions]);

  return (
    <div
      ref={containerRef}
      className={cx('bg-secondary_subtle shadow-xs flex shrink-0 flex-col overflow-hidden rounded-2xl', className)}
    >
      {/* Header */}
      <MonthlySpendComparisonChartHeader currentTotal={currentTotal} previousTotal={previousTotal} />

      {/* Chart Content */}
      <div className="shadow-xs px-5 pb-5 pt-5">
        {isLoading ? (
          <div className="flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
            <LoadingIndicator size="md" />
          </div>
        ) : chartData.length === 0 ? (
          <div
            className="text-tertiary flex items-center justify-center"
            style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}
          >
            No spending data available
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={DEFAULT_CHART_HEIGHT} className={CHART_NO_FOCUS_OUTLINE}>
              <ComposedChart
                data={chartData}
                margin={{
                  left: -10,
                  right: 20,
                  top: 5,
                  bottom: 5,
                }}
                className="text-tertiary [&_.recharts-text]:text-xs"
              >
                <defs>
                  <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  stroke="currentColor"
                  strokeDasharray="0"
                  className="text-utility-gray-100"
                />

                <XAxis
                  fill="currentColor"
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tickMargin={12}
                  ticks={xAxisTicks}
                  tickFormatter={formatOrdinal}
                />

                <YAxis
                  fill="currentColor"
                  axisLine={false}
                  tickLine={false}
                  tickMargin={8}
                  width={50}
                  tickFormatter={(value: number) => formatMoneyShort(value, { precision: 'whole' })}
                />

                <AnimationFixToolTip
                  content={<MonthlySpendComparisonChartTooltipContent />}
                  cursor={{
                    className: 'stroke-utility-brand-600 stroke-2',
                  }}
                />

                {/* Previous month spending - Dotted line (rendered first so it's behind) */}
                <Line
                  isAnimationActive={false}
                  dataKey="previousSpending"
                  name="Last Month"
                  type="monotone"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  className="text-utility-brand-400"
                  dot={false}
                  connectNulls={false}
                  activeDot={{
                    className: 'fill-bg-primary stroke-utility-brand-400 stroke-2',
                    r: 4,
                  }}
                />

                {/* Current month spending - Filled area chart */}
                <Area
                  isAnimationActive={false}
                  dataKey="spending"
                  name="This Month"
                  type="monotone"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-utility-brand-600"
                  fill="url(#spendingGradient)"
                  fillOpacity={1}
                  connectNulls={false}
                  activeDot={{
                    className: 'fill-bg-primary stroke-utility-brand-600 stroke-2',
                    r: 5,
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="mt-4 flex items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <svg width="20" height="4" className="text-utility-brand-600">
                  <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="2" />
                </svg>
                <p className="text-tertiary text-sm">This month</p>
              </div>
              <div className="flex items-center gap-2">
                <svg width="20" height="4" className="text-utility-brand-400">
                  <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2" />
                </svg>
                <p className="text-tertiary text-sm">Last month</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlySpendComparisonChart;
